/**
 * Pestaña FIXTURE: el calendario de la competencia.
 *
 * Dos formas de dibujar lo mismo, según lo que sea la fase:
 *  - **calendario** (todos contra todos, grupos): agrupado por fecha, que es
 *    como se lee un fixture impreso;
 *  - **llave** (eliminación directa): ronda por ronda, con los cruces que
 *    todavía dicen "ganador de semifinal 1".
 *
 * El resultado que se ve NO lo carga esta pantalla: lo calcula el API leyendo
 * el log de cada partido enganchado (`competition/results.ts`), y por eso cada
 * fila dice de dónde salió el suyo — del scouting, cargado a mano o por
 * reglamento. Un partido en curso muestra el parcial y se rotula "En juego".
 */
import type { Competition, FixtureMatch } from '@scout/core';
import type { MessageKey, TFunction } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { Card, Empty } from '../../components/States';
import {
  bracketRounds,
  groupByMatchday,
  isBracket,
  originLabelKey,
  roundLabelKey,
  scoreLabel,
  setsLabel,
  slotLabel,
  statusLabelKey,
  teamNames,
  type SlotLabel,
} from '../../lib/competition';
import { dateTime } from '../../lib/format';
import { matchHash } from '../../lib/router';

const PHASE_KIND_KEYS: Record<Competition['phases'][number]['kind'], MessageKey> = {
  regular: 'competition.phaseKindRegular',
  groups: 'competition.phaseKindGroups',
  playoff: 'competition.phaseKindPlayoff',
};

/** Cómo se escribe un lado del cruce: el equipo, o de dónde va a salir. */
function slotText(slot: SlotLabel, t: TFunction): string {
  if (slot.kind === 'team') return slot.name;
  if (slot.kind === 'empty') return t('competition.noRival');
  const of =
    slot.round === null || slot.position === null
      ? slot.fixtureId
      : t('competition.matchNumber', {
          round: t(roundLabelKey(slot.round, slot.round), { round: slot.round }),
          position: slot.position,
        });
  return slot.kind === 'winner'
    ? t('competition.winnerOf', { of })
    : t('competition.loserOf', { of });
}

function ResultCell({ entry, t }: { entry: FixtureMatch; t: TFunction }): JSX.Element {
  const score = scoreLabel(entry);
  if (score === null) return <span className="hint">·</span>;
  return (
    <span
      title={t('competition.resultFrom', {
        origin: t(originLabelKey(entry.result?.origin ?? 'manual')),
      })}
    >
      <strong>{score}</strong>
    </span>
  );
}

function FixtureRow({
  entry,
  names,
  byId,
  t,
}: {
  entry: FixtureMatch;
  names: Map<string, string>;
  byId: Map<string, FixtureMatch>;
  t: TFunction;
}): JSX.Element {
  const home = slotText(slotLabel(entry, 'home', names, byId), t);
  const away = slotText(slotLabel(entry, 'away', names, byId), t);
  return (
    <tr>
      <td>
        {entry.scheduledAt !== undefined ? (
          dateTime(entry.scheduledAt)
        ) : (
          <span className="hint">·</span>
        )}
        {entry.venue !== undefined ? <div className="hint">{entry.venue}</div> : null}
      </td>
      <td>
        {home} <span className="hint">{t('competition.vs')}</span> {away}
        {entry.bracket?.thirdPlace === true ? (
          <div className="hint">{t('competition.thirdPlaceMatch')}</div>
        ) : null}
      </td>
      <td className="num">
        <ResultCell entry={entry} t={t} />
      </td>
      <td>
        <span className="hint">{setsLabel(entry)}</span>
      </td>
      <td>
        {t(statusLabelKey(entry.status))}
        {entry.matchId !== undefined ? (
          <>
            {' '}
            <a href={matchHash(entry.matchId)} title={t('competition.openMatch')}>
              ↗
            </a>
          </>
        ) : null}
      </td>
    </tr>
  );
}

function FixtureTable({
  entries,
  names,
  byId,
}: {
  entries: readonly FixtureMatch[];
  names: Map<string, string>;
  byId: Map<string, FixtureMatch>;
}): JSX.Element {
  const t = useT();
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('competition.colDate')}</th>
            <th>{t('competition.colMatch')}</th>
            <th className="num">{t('competition.colResult')}</th>
            <th>{t('competition.colSets')}</th>
            <th>{t('competition.colStatusFixture')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <FixtureRow key={entry.id} entry={entry} names={names} byId={byId} t={t} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FixtureTab({
  competition,
  fixture,
}: {
  competition: Competition | null;
  fixture: readonly FixtureMatch[];
}): JSX.Element {
  const t = useT();
  const names = teamNames(competition);
  const byId = new Map(fixture.map((entry) => [entry.id, entry]));

  if (fixture.length === 0) {
    return (
      <Card>
        <Empty>{t('competition.fixtureEmpty')}</Empty>
      </Card>
    );
  }

  // Una fase por bloque: es la unidad que la federación mira (y la que se
  // genera de una).
  const phases = competition?.phases ?? [];
  const knownPhaseIds = new Set(phases.map((p) => p.id));
  const orphanIds = [...new Set(fixture.map((e) => e.phaseId))].filter(
    (id) => !knownPhaseIds.has(id),
  );

  const blocks = [
    ...phases.map((phase) => ({
      id: phase.id,
      name: phase.name,
      kindKey: PHASE_KIND_KEYS[phase.kind],
      entries: fixture.filter((e) => e.phaseId === phase.id),
    })),
    // Fases que el fixture menciona y la ficha no: se muestran igual, porque
    // esconder partidos cargados sería peor que mostrar una fase sin nombre.
    ...orphanIds.map((id) => ({
      id,
      name: id,
      kindKey: 'competition.phaseKindRegular' as MessageKey,
      entries: fixture.filter((e) => e.phaseId === id),
    })),
  ].filter((block) => block.entries.length > 0);

  return (
    <>
      {blocks.map((block) => (
        <Card
          key={block.id}
          title={block.name}
          actions={<span className="hint">{t(block.kindKey)}</span>}
        >
          {isBracket(block.entries) ? (
            bracketRounds(block.entries).map((round) => (
              <div key={round.round}>
                <h3>
                  {t(roundLabelKey(round.round, round.totalRounds), { round: round.round })}
                </h3>
                <FixtureTable entries={round.entries} names={names} byId={byId} />
              </div>
            ))
          ) : (
            groupByMatchday(block.entries).map((day) => (
              <div key={day.matchday}>
                <h3>{t('competition.matchday', { matchday: day.matchday })}</h3>
                <FixtureTable entries={day.entries} names={names} byId={byId} />
              </div>
            ))
          )}
        </Card>
      ))}
    </>
  );
}
